import React, { useState } from "react";
import { FetchBaseQueryError, skipToken } from "@reduxjs/toolkit/query/react";
import { Navigate, useParams } from "react-router";
import {
  Card,
  CopyHash,
  Link,
  LongTextLabel,
  MarkdownPreview,
  Page,
  PageNotFound,
  ValueOrPlaceholder,
} from "@sscs/ui";
import {
  buildRlPurl,
  getQueryStatus,
  getReleaseDateDisplay,
} from "@sscs/utils";
import { ApprovalStatus } from "Components";
import {
  PACKAGE_ABOUT_ROUTE,
  PERMISSION,
  STORE_CATALOG_PACKAGE_ID,
} from "Constants";
import {
  useAllowedTo,
  useAppDispatch,
  useCatalogRoute,
  useIsRestoredId,
  useProjectsRoute,
} from "Hooks";
import { useGetPackageQuery, useGetVersionsQuery } from "Services";
import CardTitle from "../../components/CardTitle/CardTitle";
import { versionDownloaded } from "../../services/softwareCatalog.api";
import styles from "./package.module.scss";
import VersionSelector from "./VersionSelector";

const PackageContent = ({
  packageId,
}: {
  packageId: string | undefined;
}): React.JSX.Element => {
  const { groupId, platform } = useParams();
  const platformRoute = useCatalogRoute(platform ? { platform } : {});
  const isRestored = useIsRestoredId(STORE_CATALOG_PACKAGE_ID, packageId);
  const { data, isFetching, isLoading, isError, error } = useGetPackageQuery(
    packageId ? { packageId } : skipToken,
  );
  const { data: versions } = useGetVersionsQuery(
    packageId ? { packageId } : skipToken,
  );
  const canCurateCatalog = useAllowedTo({
    permissions: [PERMISSION.CREATE_PACKAGE],
    groupId,
  });
  const projectsRoute = useProjectsRoute(data?.project_id, packageId);
  const dispatch = useAppDispatch();
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null,
  );
  const selectedVersion =
    versions?.versions.find(
      (version) => version.version_id === selectedVersionId,
    ) ??
    versions?.versions.find(
      (version) => version.version_id === data?.catalog_version_id,
    );

  if (isError && (error as FetchBaseQueryError)?.status === 404) {
    if (isRestored) {
      localStorage.removeItem(STORE_CATALOG_PACKAGE_ID);
      return <Navigate to={platformRoute} replace />;
    } else {
      return <PageNotFound title="Selected package not found" />;
    }
  }

  return (
    <Page.Body
      status={getQueryStatus({ data, isFetching, isError, isLoading, error })}
    >
      <div className={styles.layout}>
        {selectedVersion && versions && data && (
          <div className={styles["top-row"]}>
            <Card.Card className={styles["header-card"]}>
              <CardTitle
                fileName={selectedVersion.file_name}
                architecture={selectedVersion.software_info.architecture}
                approvalStatus={selectedVersion.approval_status}
                details={[
                  <LongTextLabel
                    key="purl"
                    className={styles.purl}
                    label={buildRlPurl(
                      data.project_name,
                      data.name,
                      selectedVersion.software_info.version,
                    )}
                  />,
                ]}
                actions={
                  <ApprovalStatus
                    status={selectedVersion.approval_status}
                    info={selectedVersion.approval_information}
                  />
                }
              >
                {selectedVersion.software_info.product}
              </CardTitle>
              <Card.Content>
                {data.description}
                <dl className={styles["software-info"]}>
                  <div>
                    <dt>Platform</dt>
                    <dd>{selectedVersion.software_info.platform}</dd>
                  </div>
                  <div>
                    <dt>Publisher</dt>
                    <dd>{selectedVersion.software_info.publisher}</dd>
                  </div>
                  <div>
                    <dt>Architecture</dt>
                    <dd>
                      <ValueOrPlaceholder
                        value={selectedVersion.software_info.architecture}
                      />
                    </dd>
                  </div>
                  <div>
                    <dt>License</dt>
                    <dd>{selectedVersion.software_info.license}</dd>
                  </div>
                  <div>
                    <dt>Release date</dt>
                    <dd>
                      <ValueOrPlaceholder
                        value={getReleaseDateDisplay(
                          selectedVersion.software_info.release_date,
                        )}
                      />
                    </dd>
                  </div>
                  <div>
                    <dt>Category</dt>
                    <dd>{selectedVersion.software_info.category}</dd>
                  </div>
                </dl>
              </Card.Content>
            </Card.Card>
            <Card.Card className={styles["download-column"]}>
              <Card.Title className={styles["download-title"]}>
                Download
              </Card.Title>
              <Card.Content className={styles["download-content"]}>
                <dl className={styles["download-info"]}>
                  <div>
                    <dt>Package downloads</dt>
                    <dd>{data.download_count}</dd>
                  </div>
                  <div>
                    <dt>Version</dt>
                    <dd>
                      <LongTextLabel
                        label={selectedVersion.software_info.version}
                      />
                    </dd>
                  </div>
                  <div>
                    <dt>SHA256</dt>
                    <dd className={styles["hash-dd"]}>
                      <LongTextLabel
                        className={styles["hash-value"]}
                        label={selectedVersion.file_sha256 ?? "--"}
                      />
                      {selectedVersion.file_sha256 && (
                        <CopyHash
                          id="version-hash"
                          sha256={selectedVersion.file_sha256}
                        />
                      )}
                    </dd>
                  </div>
                </dl>
                <VersionSelector
                  versions={versions.versions}
                  selectedVersionId={selectedVersion.version_id}
                  inCatalog={data.in_catalog}
                  onSelect={setSelectedVersionId}
                  onDownload={() =>
                    dispatch(
                      versionDownloaded({
                        packageId: data.package_id,
                        versionId: selectedVersion.version_id,
                      }),
                    )
                  }
                />
              </Card.Content>
            </Card.Card>
          </div>
        )}
        {data?.overview && (
          <Card.Card>
            <Card.Title
              actions={
                canCurateCatalog ? (
                  <Link
                    fullWidth={false}
                    to={`${projectsRoute}/${PACKAGE_ABOUT_ROUTE}`}
                  >
                    Edit
                  </Link>
                ) : undefined
              }
            >
              About
            </Card.Title>
            <Card.Content>
              <MarkdownPreview text={data.overview} />
            </Card.Content>
          </Card.Card>
        )}
      </div>
    </Page.Body>
  );
};

// Keyed by packageId so all internal state (e.g. the selected version, which is
// unique to one package) resets when the user navigates to a different package.
const Package = (): React.JSX.Element => {
  const { packageId } = useParams();
  return <PackageContent key={packageId} packageId={packageId} />;
};

export default Package;
